/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import PaymentMetohds from './PaymentMetohds';
import './payment.scss';
import PaymentForms from './PaymentForms';
import cashImg from 'assets/img/icons/cash-img.png';
import CashSaleContact from './CashSaleContact';
import customersService from 'service/customer';
import saleService from 'service/sale';
import { cashBackActions } from 'store/cash/cash.slice';
import { showAlert } from 'store/alert/alert.thunk';

export const cashSaleBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'sale',
    url: '/cash-sale'
  },
  {
    label: 'payment',
    active: true
  }
];

const CashSalePayment = () => {
  const dispatch: Dispatch<any> = useDispatch();
  const { t } = useTranslation();
  const { cashBack, selectedSalesValues, totalPrice } = useSelector(
    (state: any) => state.cashBack
  );
  const userId = useSelector((state: any) => state?.auth?.user?.id);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const legalEntityPid = useSelector(
    (state: any) => state?.auth?.legal_entity?.pid
  );
  const CurrencyPid = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency?.pid
  );

  const [paymentFormsData, setPaymentFormsData] = useState<any[]>([
    { img: cashImg, totalPrice, payment_method_pid: 5, payment_type_pid: 1 }
  ]);
  const [selectedContact, setSelectedContact] = useState<any>({});
  const [maturityDate, setMaturityDate] = useState<Date | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [amounts, setAmounts] = useState<{ [key: string]: number }>({});
  const [loading, setLoading] = useState(false);

  const { data } = useQuery(
    ['GET_CUSTOMERS_DEBTS', selectedContact?.id],
    () => {
      if (selectedContact?.id !== '') {
        const res = customersService
          .getListDebtsById(selectedContact?.id)
          .then((res: any) => res);
        return res;
      }
    },
    {
      enabled: !!selectedContact?.id
    }
  );

  const totalAmount =
    data?.total_amount?.filter((item: any) => item?.currency_pid === 2)[0]
      ?.amount || 0;

  const discountAmount = useMemo(() => {
    const amountItem = selectedSalesValues?.find(
      (item: any) => item.name === 'discount-amount' && item.value !== null
    );
    return amountItem?.value || 0;
  }, [selectedSalesValues]);

  const discountPercent = useMemo(() => {
    const percentItem = selectedSalesValues?.find(
      (item: any) => item.name === 'discount-percent' && item.value !== null
    );
    return percentItem?.value || 0;
  }, [selectedSalesValues]);

  const discountValue = useMemo(() => {
    if (discountAmount > 0) {
      return discountAmount;
    } else if (discountPercent > 0) {
      return (totalPrice * discountPercent) / 100;
    }
    return 0; // No discount
  }, [totalPrice, discountAmount, discountPercent]);

  const discountedTotal = useMemo(() => {
    return Math.max(0, totalPrice - discountValue);
  }, [totalPrice, discountValue]);

  const hasDiscount =
    selectedSalesValues.some(
      (item: any) => item.name === 'discount-amount' && item.value > 0
    ) ||
    selectedSalesValues.some(
      (item: any) => item.name === 'discount-percent' && item.value > 0
    );

  const date = new Date();
  const milliseconds = date.getTime();

  const genereteNumber = () => {
    const milliseconds = Date.now();
    const last8Digits = milliseconds % 100000000;
    const roomNumber = last8Digits.toString().padStart(8, '0');
    return parseInt(roomNumber) || 0;
  };

  const handlePayment = () => {
    const debitCreditData = {
      amount_debit: totalAmount || 0,
      debitor_id: selectedContact.id || null,
      maturity_date: maturityDate || null,
      note: noteValue,
      currency_pid: CurrencyPid,
      cashbox_pid: cashboxPid
    };
    const receiptData = {
      is_arranged: false,
      amount: totalPrice || 0,
      discount_amount: discountAmount || 0,
      customer_id: selectedContact.id || null,
      date_create: milliseconds,
      employee_id: userId,
      is_canceled: false,
      receipt_number: genereteNumber(),
      delay_employee_id: '',
      discount_percent: discountPercent || 0,
      discount_type: discountPercent > 0 ? 'percent' : 'price',
      receipt_type: 'OFFLINE',
      market_order_id: '',
      object_project_id: null,
      object_project_master_id: null,
      pid: null,
      cashbox_pid: cashboxPid,
      currency_pid: 2,
      legal_entity_pid: legalEntityPid,
      status_pid: 7,
      has_installment: false
    };

    const saleTransactionsData = paymentFormsData.map((item: any, index) => ({
      amount_in: amounts[index] || totalPrice,
      amount_out: 0,
      cashbox_id: cashboxId,
      date_create: milliseconds,
      employee_id: userId,
      is_canceled: false,
      note: '',
      show_cashflow: true,
      show_pl: true,
      cashbox_pid: cashboxPid,
      currency_pid: 2,
      legal_entity_pid: legalEntityPid,
      payment_method_pid: item?.payment_method_pid || 5,
      payment_purpose_pid: 1,
      payment_type_pid: item?.payment_type_pid || 1,
      original_currency_pid: null
    }));

    const discount = selectedSalesValues?.[0];
    const discountType = discount?.name;
    const discountValue = discount?.value || 0;

    const hasWholesalePrice = selectedSalesValues.some(
      (item: any) => item.name === 'wholesale-price' && item.value === true
    );

    /* prettier-ignore */
    const subtotal = cashBack?.reduce(
      (sum: number, p: any) =>
      sum +
        (p.payment_amount_total
          ? p.payment_amount_total
          : hasWholesalePrice
            ? p.price_wholesale
            : p.price || 0 * 1),
      0
    );

    const cashSaleData = cashBack?.map((item: any) => {
      const price = hasWholesalePrice
        ? item.price_wholesale || 0
        : item.price || 0;

      const defTotal = 1 * price;
      const productSubtotal = item?.payment_amount_total || defTotal;

      let discount_amount = 0;
      let payment_amount_total = productSubtotal;

      if (discountType === 'discount-percent') {
        discount_amount =
          (productSubtotal * (discountValue / cashBack.length)) / 100;
        payment_amount_total = productSubtotal - discount_amount;
      } else if (discountType === 'discount-amount' && subtotal > 0) {
        // distribute equally across all products
        const perItemDiscount = discountValue / cashBack.length;

        discount_amount = perItemDiscount;
        payment_amount_total = productSubtotal - perItemDiscount;
      }

      return {
        amount_returned: 0,
        currency_rate: 0,
        customer_id: selectedContact.id || null,
        date_create: milliseconds,
        discount_amount: 0, // donali skidkani qiymati
        discount_percentage: 0, // donali skidkani qiymati
        employee_id: userId,
        is_cancaled: false,
        is_vat: false,
        vat: item?.vat || 0,
        payment_amount: item?.payment_amount || price,
        payment_amount_total: payment_amount_total,
        price_in: item?.price_in || 0,
        price_in_original: item?.price_in || 0,
        price_wholesale: item?.price_whosale || 0,
        price_wholesale_original: item?.price_whosale || 0,
        price_original: item?.price || 0,
        quantity: item?.updated_amount || 1,
        quantity_left: 0,
        quantity_returned: 0,
        is_arranged: false,
        negative_sale: false,
        is_wholesale:
          selectedSalesValues.some(
            (item: any) => item.name === 'wholesale-price'
          ) || false,
        product: {
          barcode: item?.barcode
        },
        cashbox_pid: cashboxPid,
        currency_pid: 2,
        legal_entity_pid: legalEntityPid,
        product_pid: item?.pid || 0,
        nomenclature_pid: item?.nomenclature_pid || 0,
        status_pid: 7,
        price: item?.price || 0
      };
    });
    const paymentData = {
      items: [
        {
          debit_credit: selectedContact?.id ? debitCreditData : null,
          receipt: receiptData,
          sale_transactions: saleTransactionsData,
          sales: cashSaleData
        }
      ]
    };

    if (paymentData) {
      setLoading(true);
      saleService
        .createSale(paymentData)
        .then((res: any) => {
          if (res) {
            setLoading(false);
            dispatch(
              showAlert({
                title: `${t('payment_complited_successfuly')}`,
                type: 'success'
              })
            );
            window.history.back();
            dispatch(cashBackActions.clearCashBackList());
          }
        })
        .catch(() => setLoading(false));
    }
  };

  return (
    <div className="payment-container">
      <PageBreadcrumb items={cashSaleBreadcrumbItems} />
      <h2 className="mb-5">{t('payment')}</h2>

      <div className="payment">
        <div className="payment-details">
          <PaymentMetohds
            setPaymentFormsData={setPaymentFormsData}
            paymentFormsData={paymentFormsData}
          />
          <PaymentForms
            paymentFormsData={paymentFormsData}
            setPaymentFormsData={setPaymentFormsData}
            amounts={amounts}
            setAmounts={setAmounts}
          />
          <CashSaleContact
            maturityDate={maturityDate}
            setMaturityDate={setMaturityDate}
            totalAmount={totalAmount}
            selectedContact={selectedContact}
            setSelectedContact={setSelectedContact}
            setNoteValue={setNoteValue}
            noteValue={noteValue}
          />
        </div>

        <div className="cashback-total">
          <div className="total-items-info">
            <p className="total-title">
              {t('products')} ({cashBack?.length || 0})
            </p>
            <p className="total-price">
              {new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(totalPrice)
                .replace(/,/g, ' ')}
            </p>
          </div>

          {hasDiscount && (
            <div className="total-discount-info">
              <p className="total-discount-title">{t('discount')}</p>
              <p className="total-discount-price">
                {new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(discountValue)
                  .replace(/,/g, ' ')}
              </p>
            </div>
          )}

          <div className="total-info">
            <p className="info-title">{t('result')}</p>
            <p className="info-price">
              {new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(discountedTotal)
                .replace(/,/g, ' ')}
            </p>
          </div>

          <div className="payment-buttons">
            <Button
              disabled={loading}
              style={{ height: '48px', width: '100%' }}
              variant="phoenix-secondary"
              onClick={() => {
                window.history.back();
              }}
            >
              {t('back')}
            </Button>
            <Button
              onClick={handlePayment}
              style={{ height: '48px', width: '100%' }}
              variant="primary"
              loading={loading}
              loadingPosition="start"
            >
              {t('payment')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashSalePayment;
